/**
 * Turkish message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal sen (locked in l10n/locales.md) —
 * imperatives in sen form (gönder, aç, dene), possessives -in/-ın
 * (cihazın, telefonun).
 *
 * Platform terms follow Turkish UI conventions (Tema for theme, sohbet
 * for conversation, eşleştir for pair, temizle for dismiss, pano for
 * clipboard); "Glance" is the untranslated feature brand name
 * (glossary D1) and "klip"/"klipler" is a locked loanword (D2).
 *
 * Plural notes: Turkish is a {one, other} CLDR locale, but a numeral is
 * always followed by the SINGULAR noun (5 cihaz, never 5 cihazlar), so
 * both forms carry the same singular noun; the split stays structural
 * and every count still routes through `selectPlural("tr", …)`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Bağlandı",
    statusReconnecting: "Yeniden bağlanılıyor…",
    statusDisconnected: "Bağlantı kesildi",
    openSettings: "Ayarları aç",
    pairedFallbackDevice: "telefonun",
    publicModeToggleEnable: "Bildirim içeriğini gizle",
    publicModeToggleDisable: "Bildirim içeriğini göster",
  },

  notifications: {
    replyAriaLabel: "Yanıtla",
    replyPlaceholder: "Yanıtla…",
    replySend: "Gönder",
    sent: "Gönderildi",
    failedToSend: "Gönderilemedi",
    unread: "Okunmadı",
    dismiss: "Temizle",
    notificationImageAlt: "Bildirim resmi",
  },

  conversations: {
    replyAriaLabel: "Yanıtla",
    replyPlaceholder: "Yanıtla…",
    replySend: "Gönder",
    sent: "Gönderildi",
    failedToSend: "Gönderilemedi",
    dismiss: "Sohbeti temizle",
    messagesListLabel: "Bu sohbetteki mesajlar",
    deviceOwner: "Sen",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Okunmamış mesaj yok"
        : `${count} ${selectPlural("tr", count, {
            one: "okunmamış mesaj",
            other: "okunmamış mesaj",
          })}`,
  },

  list: {
    today: "Bugün",
    yesterday: "Dün",
    searchNotifications: "Bildirimlerde ara",
    clearSearch: "Aramayı temizle",
    clearAll: "Tümünü temizle",
    markAllRead: "Tümünü okundu işaretle",
    groupCheckboxLabel: "Grupla",
    noNotificationsTitle: "Henüz bildirim yok",
    noNotificationsDesc: "Telefonundan yansıtılan yeni mesajlar burada görünecek.",
    noMatchesTitle: "Eşleşme yok",
    notificationsList: "Bildirimler",
    privacyAppList: "Uygulamaya göre bildirimler",
    clear: "Temizle",
    // Turkish keeps the singular noun after a numeral, so both plural
    // forms carry the same "eşleşmeyi" — count still routes the selection.
    clearMatches: (count: number): string =>
      `${count} ${selectPlural("tr", count, { one: "eşleşmeyi", other: "eşleşmeyi" })} temizle`,
    noMatchesDesc: (query: string): string => `“${query}” ile eşleşen bildirim yok.`,
    appNotificationsGroup: (appName: string): string => `${appName} bildirimleri`,
    dismissAllFrom: (appName: string): string => `${appName} bildirimlerinin tümünü temizle`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("tr", count, { one: "mesaj", other: "mesaj" })
          : kind === "calls"
            ? selectPlural("tr", count, { one: "arama", other: "arama" })
            : selectPlural("tr", count, { one: "bildirim", other: "bildirim" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "temizle",
    kbdNavigate: "gezin",
    kbdOrSeparator: "veya",
  },

  settings: {
    back: "Bildirimlere dön",
    settings: "Ayarlar",
    general: "Genel",
    theme: "Tema",
    themeAuto: "Otomatik",
    themeLight: "Açık",
    themeDark: "Koyu",
    language: "Dil",
    languageSystemDefault: "Sistem varsayılanı",
    notificationsSection: "Bildirimler",
    osNotifications: "Sistem bildirimleri",
    callNotifications: "Arama bildirimleri",
    privacySecurity: "Gizlilik",
    analyticsToggle: "Anonim kullanım istatistikleri",
    analyticsToggleDescription:
      "Yalnızca sayılar ve kilometre taşları — bildirimlerin asla gönderilmez.",
    statusConnected: "Bağlandı",
    statusReconnecting: "Yeniden bağlanılıyor…",
    statusDisconnected: "Bağlantı kesildi",
    disconnectFromPhone: "Telefonla bağlantıyı kes",
    disconnectConfirm: "Emin misin? Bu, cihazının eşleştirmesini kaldırır.",
    yesDisconnect: "Evet, bağlantıyı kes",
    cancel: "İptal",
    version: "Sürüm",
    encryption: "Şifreleme",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Yardım mı lazım? __EMAIL__ adresine e-posta gönder",
    disconnectFailed: "Bağlantı kesilemedi — lütfen tekrar dene",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Bağlantı kesildi ancak yerel temizleme başarısız oldu: ${errorMsg}. Lütfen sayfayı yeniden yükle.`,
    disconnectLocalCleanupFailed:
      "Bağlantı kesildi ancak yerel temizleme başarısız oldu. Lütfen sayfayı yeniden yükle.",
  },

  pairing: {
    enterCodeInstruction: "Bu kodu Reflecto uygulamasına gir",
    pairedSuccessfully: "Başarıyla eşleştirildi!",
    pairingFailed: "Eşleştirme başarısız",
    requestingPairingCode: "Eşleştirme kodu isteniyor…",
    codeExpired: "Kodun süresi doldu",
    tryAgain: "Tekrar Dene",
    failedToStartPairing: "Eşleştirme başlatılamadı",
    installHeading: "Uygulama henüz sende yok mu?",
    installScanHint: "Telefonunun kamerasıyla tara",
    installQrLabel: "Reflecto'yu telefonuna kurmak için QR kodu",
    capabilitiesHeading: "Eşleştirdikten sonra:",
    capabilityMirror: "Telefonunun bildirimlerini burada görürsün",
    capabilityReply: "Telefonu eline almadan yanıt verirsin",
    capabilityShare: "Panoyu ve dosyaları iki yönde paylaşırsın",
    encryptionNote: "Uçtan uca şifreli. Sunucu içeriğini asla görmez.",
  },

  dnd: {
    title: "Rahatsız etmeyin",
    description: "Bu cihazdaki bildirimleri bir saatliğine duraklat.",
    snooze1h: "1 saat duraklat",
    snoozeActiveUntil: (formattedTime: string): string => `${formattedTime} saatine kadar etkin`,
    cancelSnooze: "İptal",
  },

  connectors: {
    title: "Bağlı hizmetler",
    emptyBody:
      "Henüz bağlı hizmet yok. ChatGPT veya Claude'u kendi bağlayıcı ayarlarından bağla, ardından telefonunda kodu onayla.",
    scopeSend: "Bildirim gönderebilir",
    added: (date: string): string => `${date} tarihinde eklendi`,
    lastUsed: (relative: string): string => `En son ${relative} kullanıldı`,
    neverUsed: "Hiç kullanılmadı",
    revoke: "Bağlantıyı kes",
    revokeConfirm: (label: string): string =>
      `${label} bağlantısı kesilsin mi? Erişimi hemen kaybeder.`,
    revokeAction: "Bağlantıyı kes",
    revokeConfirmLabel: (label: string): string => `${label} bağlantısını kes`,
    cancel: "İptal",
    loadError: "Bağlı hizmetler yüklenemedi. Lütfen tekrar dene.",
    revokeError: "Hizmetin bağlantısı kesilemedi. Lütfen tekrar dene.",
    retry: "Yeniden dene",
  },

  safetyNumber: {
    rowLabel: "Şifrelemeyi doğrula",
    rowSublabel: "Telefondaki güvenlik numaralarını karşılaştırın",
    cardTitle: "Güvenlik numarası",
    caption: (deviceLabel: string): string =>
      `${deviceLabel} üzerinde gösterilen numarayla karşılaştırın. Her ikisi de eşleşirse, uçtan uca şifrelemeleriniz bozulmamıştır.`,
    repairNote: "Bu numara, bir cihazı yeniden eşleştirdiğinizde değişir.",
    copy: "Kopyala",
    copied: "Kopyalandı",
    maskedHint: "Gizlilik modu açıkken gizli",
  },

  feedback: {
    title: "Geri bildirim gönder",
    close: "Kapat",
    messageLabel: "Mesajın",
    messagePlaceholder:
      "Bir hata mı buldun, bir fikrin mi var ya da bir şey yanlış mı hissettiriyor? Bize anlat.",
    categoryLabel: "Kategori",
    categoryBug: "Hata",
    categoryIdea: "Fikir",
    categoryOther: "Diğer",
    emailLabel: "E-posta (isteğe bağlı, yalnızca yanıt istiyorsan)",
    emailPlaceholder: "sen@ornek.com",
    emailHelp: "Seni hiçbir listeye eklemeyiz.",
    disclosure:
      "Mesajın Reflecto'nun sunucusuna gönderilir ve dahili sorun izleyicimize kaydedilir. Raporları ilişkilendirebilmek için uygulama sürümünü ve eşleştirme sırasında atanan bir cihaz kimliğini ekleriz. Bildirimlerini, kişilerini veya herhangi bir kişisel veriyi görmeyiz. E-posta isteğe bağlıdır — yalnızca yanıt istiyorsan ver. Seni hiçbir listeye eklemeyiz.",
    submit: "Geri bildirim gönder",
    sending: "Gönderiliyor…",
    success: "Teşekkürler — bir insan tarafından, genellikle 24 saat içinde okunur",
    error: "Gönderilemedi. Lütfen tekrar dene.",
    done: "Bitti",
    sendFeedback: "Geri bildirim gönder",
  },

  uninstall: {
    headline: "Reflecto uzantısı kaldırıldı",
    body: "Hiçbir zaman hesap yoktu ve uzantı eşleştirmeni bu bilgisayarda tutuyordu, dolayısıyla temizlemen gereken bir şey kalmadı.",
    ask: "Otuz saniyen varsa, neyin ters gittiğini öğrenmek isteriz.",
    categoryLegend: "Bu ne tür bir geri bildirim?",
    categoryBug: "Bir şey bozuldu",
    categoryIdea: "İhtiyacım olan bir şey eksikti",
    categoryOther: "Başka bir şey",
    messageLabel: "Ne ters gitti?",
    messagePlaceholder: "Birkaç saat sonra bildirimler gelmeyi bıraktı.",
    messageRequired: "Önce bir cümle yaz, sonra gönder.",
    charactersLeft: (count: number): string => `${count} karakter kaldı`,
    emailLabel: "E-posta (isteğe bağlı)",
    emailPlaceholder: "sen@ornek.com",
    emailHelp: "Yalnızca sana yanıt vermek için kullanılır. Başka hiçbir şey için değil.",
    submit: "Geri bildirim gönder",
    sending: "Gönderiliyor…",
    retry: "Tekrar dene",
    privacyNote:
      "Bu form hiçbir cihaza bağlı değil. İçinde bildirimlerin, panon veya dosyaların yok. E-posta isteğe bağlıdır ve yalnızca yanıt vermek için kullanılır.",
    success: "Teşekkürler. Bunların her birini bir insan okuyor.",
    errorGeneric: "Gönderilemedi. Mesajın hâlâ burada.",
    errorRateLimited:
      "Son zamanlarda birkaç mesaj gönderdin. Yaklaşık bir saat sonra tekrar dene. Yazdıkların burada kalır.",
    footerWebApp: "Reflecto web uygulaması uzantı olmadan da çalışır.",
    footerWebAppLink: "Web uygulamasını aç",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Kapat",
    aiSummaryTag: "Yapay zeka özeti",
    errorAi: "Şu anda özet oluşturulamadı.",
    retryCta: "Tekrar dene",
    downloadHeading: "Yapay zeka özetleri al",
    downloadBody:
      "Cihazında çalışan bir yapay zeka özetiyle kaçırdıklarını yakala. Tamamen cihazında çalışır.",
    downloadCta: "Modeli indir",
    downloadPrivacyNote: "Tek seferlik bir indirme. Sunucu bildirimlerini asla görmez.",
    downloadingHeading: "Model indiriliyor…",
    caughtUp: "Her şeyi gördün — şu anda ilgilenmen gereken bir şey yok.",
    needsYou: "İlgilenmen gerekenler",
    quiet: "Diğer",
    scanning: (pending: number): string => `${pending} öğe özetleniyor…`,
    jump: "Bildirimi aç",
    affordanceReply: "Yanıtla",
    affordanceOpen: "Aç",
    affordanceCopyOtp: "Kodu kopyala",
    buttonLabel: "Glance",
  },

  update: {
    title: "Güncelleme mevcut",
    description: "Reflecto'nun yeni bir sürümü hazır. Güncellemek için yeniden yükle.",
    reload: "Yeniden yükle",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Bu cihaza “${name}” adını verdik.`,
    cardLooksGood: "İyi görünüyor",
    cardEdit: "Düzenle",
    cardAriaLabel: "Cihaz adını onayla",
    renameTitle: "Bu cihazı yeniden adlandır",
    renameLabel: "Cihaz adı",
    renameSave: "Kaydet",
    renameCancel: "İptal",
    renameFailed: "Yeniden adlandırılamadı. Lütfen tekrar dene.",
  },

  collision: {
    title: (name: string): string => `Zaten bir “${name}” var`,
    subtitle: "Buna ne diyelim?",
    chipWork: (name: string): string => `İş ${name}`,
    chipHome: (name: string): string => `Ev ${name}`,
    chipPersonal: (name: string): string => `Kişisel ${name}`,
    customLabel: "Özel ad",
    save: "Kaydet",
    useAnyway: "Yine de özgün adı kullan",
    saveFailed: "Yeni ad kaydedilemedi. Lütfen tekrar dene.",
    ariaLabel: "Yinelenen cihaz adını çöz",
  },

  clipboard: {
    title: "Pano",
    emptyTitle: "Henüz klip yok",
    empty: "Diğer cihazlarından gelen klipler burada görünür. Kopyalamak için dokun.",
    from: (label: string): string => `kaynak: ${label}`,
    to: (label: string): string => `hedef: ${label}`,
    copy: "Panoya kopyala",
    copied: "Kopyalandı",
    reveal: "Göster",
    hide: "Gizle",
    maskedHint: "İçerik gizli — göstermek için Göster'e bas",
    sensitiveChip: "Hassas",
    cloudChip: "API jetonundan",
    delete: "Klibi sil",
    sendPlaceholder: "Göndermek için metin yapıştır…",
    sendCta: "Gönder",
    sendSensitiveToggle: "Hassas olarak işaretle",
    sendSuccess: (n: number): string =>
      `${n} ${selectPlural("tr", n, { one: "cihaza", other: "cihaza" })} gönderildi.`,
    sendPartial: (n: number, total: number): string =>
      `${total} ${selectPlural("tr", total, { one: "cihazdan", other: "cihazdan" })} ${n} tanesine ulaşıldı.`,
    sendNoDevices: "Gönderilecek eşleştirilmiş cihaz yok.",
    sendFailed: "Gönderilemedi. Lütfen tekrar dene.",
    sendEmpty: "Gönderilecek bir şey yok.",
    dockRecentHeading: "Son",
    dockEmptyClipboard: "Panon boş.",
    clearAll: "Tümünü temizle",
    clearAllConfirm: "Evet, tümünü temizle",
    clearAllCancel: "İptal",
    shareTitle: "Cihazlarına gönder",
    shareReview:
      "Ne gönderdiğini gözden geçir. Eşleştirilmiş cihazlarına uçtan uca şifreli olarak gider.",
    shareSend: "Gönder",
    shareCancel: "İptal",
    shareNotPaired: "Paylaşılan metni göndermek için önce bir cihaz eşleştir.",
    shareOpenApp: "Reflecto'yu aç",
    pasteFromClipboard: "Panodan yapıştır",
    readFailed: "Pano okunamadı — aşağıya yapıştırın.",
  },
  install: {
    title: "Reflecto'yu yükle",
    body: "Daha hızlı erişim için ana ekranına ekle.",
    cta: "Yükle",
    dismissAria: "Yükleme istemini kapat",
  },
  nav: {
    home: "Ana sayfa",
    clipboard: "Pano",
    files: "Dosyalar",
    ariaLabel: "Ana gezinme",
    fileHiddenPublicMode: "Gizlilik modu açıkken gizlenir.",
  },
  common: {
    loading: "Yükleniyor",
  },
  errors: {
    notPaired: "Eşleştirilmemiş",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} (${os})`,
    webFallback: "Web Tarayıcısı",
  },

  palette: {
    placeholder: "Bir komut yaz…",
    noResults: "Eşleşen komut yok",
    ariaLabel: "Komut paleti",
    clearAll: "Tüm bildirimleri temizle",
    glance: "Glance özeti",
    cancelSnooze: "Duraklatmayı iptal et",
    copyClip: "Alınan son klibi kopyala",
  },

  fileTransfer: {
    sendTitle: "Dosya gönder",
    sendChooseFile: "Dosya seç",
    sendDeviceLabel: "Şuraya gönder",
    sendPhoneFallbackName: "Telefon",
    send: "Dosyayı gönder",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Hazırlanıyor…" : `Hazırlanıyor — ${pct}%`,
    sendDelivering: "Telefonuna iletiliyor…",
    sendWaitingAccept: "Telefonunun kabul etmesi bekleniyor…",
    sendWaitingAppOpen: "Kabul etmek için telefonunda Reflecto'yu aç",
    sendWaitingAppOpenHint:
      "Telefonun anlık bildirim alamıyor, bu yüzden bu dosyayı ancak Reflecto açıkken gösterecek.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `${partCount} parçadan ${partNumber}. parça yükleniyor…`,
    sendDone: "Gönderildi.",
    sendFailedRejected: "Telefonun dosyayı reddetti.",
    sendFailedNoAnswer: "Telefonundan yanıt yok.",
    sendFailedUploadFailed: "Yükleme başarısız oldu. Lütfen tekrar dene.",
    sendFailedCancelled: "İptal edildi.",
    sendFailedGeneric: "Bir şeyler ters gitti. Lütfen tekrar dene.",
    sendTooLarge: (maxMiB: number): string => `Dosya çok büyük (en fazla ${maxMiB} MiB).`,
    sendKeepOpenHint: "Aktarım bitene kadar bu sekmeyi açık tut.",
    sendAnother: "Başka bir dosya gönder",
    sendNoDevice: "Dosya göndermek için önce bir telefon eşleştir.",
    sendCancel: "İptal",
    sendPeerProgress: (chunksDone: number): string => `Telefon ${chunksDone} blok aldı.`,

    receiveTitle: "Gelen dosyalar",
    receiveOfferFrom: (sender: string): string => `${sender} sana bir dosya göndermek istiyor`,
    receiveAccept: "Kabul et",
    receiveDecline: "Reddet",
    receiveReceiving: (n: number, total: number): string =>
      `${total} parçadan ${n}. parça alınıyor…`,
    receiveDone: "Kaydedildi.",
    receiveFailed: "Aktarım başarısız oldu.",
    receiveCancelled: "İptal edildi.",
    receiveCancel: "İptal",

    errorChunkAuthFail: "Aktarım başarısız oldu — veriler doğrulanamadı. Tekrar dene.",
    errorHashMismatch:
      "Aktarım başarısız oldu — dosya bütünlük denetiminden geçemedi. Tekrar dene.",
    errorStorageFull: "Bu aktarım için yeterli depolama alanı yok.",
    errorExpired: "Aktarımın süresi doldu.",
    errorQuotaExceeded: "Günlük aktarım sınırına ulaşıldı — yarın tekrar dene.",
    errorInternal: "Aktarım başarısız oldu — bir şeyler ters gitti.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `%${pct}`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Kapat",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Aktarım ilerlemesi",
    outgoing: (n: number): string => `Giden (${n})`,
    incoming: (n: number): string => `Gelen (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Tümünü kabul et",
    dropTitle: "Buraya sürükleyip bırakın",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `${size} boyutuna kadar dosyalar.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Buraya sürükleyip bırakın veya gönderilecek bir dosya seçin",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Doğrudan",
    transportDirectHint: "Cihazdan cihaza gönderildi — sunucularımızda hiçbir zaman saklanmaz.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Sunucu üzerinden",
    transportRelayHint:
      "Sunucularımız üzerinden aktarıldı, hâlâ uçtan uca şifreli — içeriğini okuyamayız.",
    directBadge: "Doğrudan",
  },
  time: {
    justNow: "az önce",
    minutesAgo: (count: number): string => `${count} dk önce`,
    hoursAgo: (count: number): string => `${count} sa önce`,
  },
} satisfies MessagesShape;
